/**
 * LiftStatusDashboard Component
 *
 * Real-time dashboard showing lift occupancy status.
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import { formatDuration } from '@/utils/duration';
import {
  useLiftStatus,
  type LiftStatusItem,
  type LiftOccupancy,
} from '@/hooks/useLifts';

const OCCUPANCY_COLORS: Record<LiftOccupancy, 'success' | 'primary' | 'warning' | 'error'> = {
  free: 'success',
  in_use: 'primary',
  paused: 'warning',
  maintenance: 'error',
};

export function LiftStatusDashboard() {
  const { t } = useTranslation('equipment');
  const { data: lifts, isLoading, error } = useLiftStatus();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t('lifts.loadFailed')}</Alert>;
  }

  if (!lifts || lifts.length === 0) {
    return (
      <Alert severity="info">
        <Typography fontWeight={600}>{t('liftStatus.noLifts')}</Typography>
        <Typography variant="body2">{t('liftStatus.noLiftsDesc')}</Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Stack mb={3}>
        <Typography variant="h6">{t('liftStatus.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('liftStatus.subtitle')}
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        {lifts.map((lift) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={lift.id}>
            <LiftStatusCard lift={lift} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function LiftStatusCard({ lift }: { lift: LiftStatusItem }) {
  const { t } = useTranslation('equipment');

  const occupancyLabelKey = {
    free: 'liftStatus.free',
    in_use: 'liftStatus.inUse',
    paused: 'liftStatus.paused',
    maintenance: 'liftStatus.maintenance',
  }[lift.occupancy];

  return (
    <Card
      variant="outlined"
      sx={{
        borderLeft: 4,
        borderLeftColor: `${OCCUPANCY_COLORS[lift.occupancy]}.main`,
      }}
    >
      <CardActionArea disabled={!lift.currentBooking}>
        <CardContent>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                {lift.name}
              </Typography>
              <Chip
                size="small"
                label={t(occupancyLabelKey)}
                color={OCCUPANCY_COLORS[lift.occupancy]}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {lift.identifier}
            </Typography>

            {lift.currentBooking && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {lift.currentBooking.customerName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {lift.currentBooking.vehicleReg}
                  {lift.currentBooking.vehicleMakeModel
                    ? ` — ${lift.currentBooking.vehicleMakeModel}`
                    : ''}
                </Typography>
                {lift.currentBooking.estimatedDurationHours && (
                  <Typography variant="caption" color="text.secondary">
                    {t('liftStatus.estimated')}: {formatDuration(lift.currentBooking.estimatedDurationHours)}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
